import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import { IMAGE_BASE } from "../Config";
import { fetchLatestContentByType } from "../services/ContentService";
import { getCachedLatestArticles } from "../utils/homePageCache";

interface LatestArticlesSectionProps {
  articles?: any[];
  useCached?: boolean;
}

const LatestArticlesSection: React.FC<LatestArticlesSectionProps> = ({ articles: propArticles, useCached = false }) => {
  const [articles, setArticles] = useState<any[]>(propArticles || []);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // If articles were provided as props, use those instead of fetching
    if (propArticles && propArticles.length > 0) {
      setArticles(propArticles);
      setLoading(false);
      return;
    }
    
    const fetchArticles = async () => {
      try {
        setLoading(true);
        let data;
        if (useCached) {
          data = await getCachedLatestArticles(3);
        } else {
          data = await fetchLatestContentByType('article', 3);
          // Store in cache for future use
          const { cacheSet } = await import('../utils/cacheUtil');
          await cacheSet(`latest_articles_3`, data, 21600); // 6 hours TTL
        }
        setArticles(data);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticles();
  }, [propArticles]);
  
  // Check if we have articles
  const hasArticles = articles && articles.length > 0;

  // Use all articles (already limited to 3 in the API call)
  const latestArticles = articles;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <section className="py-5" style={{ background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)" }}>
      <Container>
        <div className="text-center mb-4">
          <h2 className="mb-2" style={{ color: "#810081" }}>Latest Articles</h2>
          <p className="text-muted">Stay updated with our newest content</p>
          <div 
            className="mx-auto mb-4" 
            style={{ 
              width: "60px", 
              height: "3px", 
              background: "linear-gradient(45deg, #810081, #FFD600)", 
              borderRadius: "2px" 
            }}
          ></div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" style={{ color: "#810081" }} />
            <p className="mt-2 text-muted">Loading articles...</p>
          </div>
        ) : (
          <Row className="g-4">
            {hasArticles ? latestArticles.map((article) => (
            <Col key={article._id} md={4}>
              <Card 
                className="h-100 border-0 shadow-sm" 
                style={{ 
                  borderRadius: '15px',
                  transition: "all 0.3s ease",
                  overflow: "hidden"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-5px)";
                  e.currentTarget.style.boxShadow = "0 10px 20px rgba(129, 0, 129, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                }}
              >
                <div style={{ position: "relative", height: "180px", overflow: "hidden" }}>
                  {article.imageLink ? (
                    <Card.Img 
                      variant="top" 
                      src={IMAGE_BASE + article.imageLink} 
                      style={{ height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div 
                      className="d-flex align-items-center justify-content-center"
                      style={{ 
                        height: "100%", 
                        background: "linear-gradient(135deg, #FFD600 0%, #810081 100%)" 
                      }}
                    >
                      <i className="bi bi-journal-text text-white" style={{ fontSize: "2rem" }}></i>
                    </div>
                  )}
                  <div 
                    style={{ 
                      position: "absolute", 
                      top: "10px", 
                      right: "10px",
                      background: "rgba(255,255,255,0.9)",
                      padding: "5px 10px",
                      borderRadius: "20px",
                      fontSize: "0.8rem",
                      color: "#810081"
                    }}
                  >
                    <i className="bi bi-calendar2 me-1"></i>
                    {formatDate(article.createdAt)}
                  </div>
                </div>
                <Card.Body>
                  <Card.Title style={{ fontSize: "1.1rem", fontWeight: "600", color: "#810081" }}>
                    {article.title}
                  </Card.Title>
                  <Card.Text className="text-muted" style={{ fontSize: "0.9rem" }}>
                    {article.content && article.content.length > 100 
                      ? `${article.content.substring(0, 100).replace(/<[^>]*>/g, '')}...` 
                      : article.content?.replace(/<[^>]*>/g, '')}
                  </Card.Text>
                  <Link 
                    to={`/article/${article._id}`} 
                    className="btn btn-sm mt-2"
                    style={{ 
                      backgroundColor: "#FFD600", 
                      color: "#810081", 
                      borderRadius: "20px",
                      padding: "5px 15px",
                      fontWeight: "500"
                    }}
                  >
                    Read More <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          )) : (
            <Col md={12} className="text-center py-4">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(129, 0, 129, 0.1)'
                }}
              >
                <i className="bi bi-journal-text" style={{ fontSize: '2rem', color: '#810081' }}></i>
              </div>
              <h4 style={{ color: '#810081' }}>No Articles Yet</h4>
              <p className="text-muted">Check back soon for new articles</p>
            </Col>
            )}
          </Row>
        )}

        <div className="text-center mt-4">
          <Link 
            to="/articles" 
            className="btn px-4 py-2"
            style={{ 
              backgroundColor: "#810081", 
              color: "white", 
              borderRadius: "30px",
              fontWeight: "500"
            }}
          >
            View All Articles <i className="bi bi-arrow-right ms-2"></i>
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default LatestArticlesSection;